import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, UserPlus, Phone, MapPin, ChevronRight } from "lucide-react";
import { ClientForm, EMPTY_CLIENT as empty } from "@/components/ClientForm";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = (query = "") => api.get("/clients", { params: query ? { q: query } : {} }).then(({ data }) => setClients(data));
  useEffect(() => { load(); if (new URLSearchParams(window.location.search).get("new") === "1") { setForm(empty); setOpen(true); } }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Cliente cadastrado!");
      setOpen(false); setForm(empty);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Clientes</h1>
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-xl" data-testid="new-client-btn"><UserPlus className="h-4 w-4 mr-1" /> Novo cliente</Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF/CNPJ, telefone ou código..." className="pl-10 h-12" data-testid="client-search-input" />
      </div>

      <div className="flex gap-4 text-xs text-muted-foreground" data-testid="clients-secondary-links"><Link to="/maintenance" className="hover:text-foreground">Manutenção & garantias</Link><Link to="/contracts" className="hover:text-foreground">Contratos</Link><Link to="/crm" className="hover:text-foreground">Oportunidades</Link></div>

      {clients.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><UserPlus className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>Nenhum cliente encontrado.</p></Card>
      ) : (
        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="clients-list">
          <div className="hidden sm:grid grid-cols-[1.6fr_1fr_1fr_0.9fr] gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><span>Nome</span><span>Telefone</span><span>Cidade</span><span className="text-right">Último serviço</span></div>
          {clients.map((c) => (
            <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-card-${c.id}`}
              className="w-full text-left grid grid-cols-[1fr_auto] sm:grid-cols-[1.6fr_1fr_1fr_0.9fr] gap-x-3 items-center px-4 py-3 hover:bg-accent/50 transition-colors">
              <span className="font-medium text-sm truncate">{c.name}</span>
              <span className="text-sm text-muted-foreground truncate hidden sm:block">{c.phone || c.whatsapp || "—"}</span>
              <span className="text-sm text-muted-foreground truncate hidden sm:block">{c.city || "—"}</span>
              <span className="text-sm text-muted-foreground text-right">{c.last_service || "—"}</span>
              <span className="sm:hidden text-xs text-muted-foreground col-span-2 truncate">{[c.phone || c.whatsapp, c.city].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo cliente</DialogTitle></DialogHeader>
          <ClientForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-client-btn">{saving ? "Salvando..." : "Salvar cliente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
